/**
 * Cookie Consent Service
 * Manages cookie consent state and provides methods for cookie operations
 * Complies with EU GDPR and ePrivacy Directive requirements
 */

const CONSENT_STORAGE_KEY = "cookieConsent";
const CONSENT_COOKIE_NAME = "cookie_consent";
const CONSENT_VERSION = "1.0";
const COOKIE_EXPIRY_DAYS = 365;

/**
 * Get consent preferences from storage
 * @returns {Object|null} Consent preferences object or null if not set
 */
export const getConsent = () => {
	try {
		const stored = localStorage.getItem(CONSENT_STORAGE_KEY);
		if (stored) {
			const consent = JSON.parse(stored);
			// Validate consent object structure
			if (consent && typeof consent === "object" && "version" in consent) {
				return consent;
			}
		}
	} catch (error) {
		console.error("[cookieService][getConsent] - Error reading consent:", error);
	}
	return null;
};

/**
 * Set consent preferences
 * @param {Object} preferences - Consent preferences object
 * @param {boolean} preferences.essential - Essential cookies (always true)
 * @param {boolean} preferences.analytics - Analytics cookies
 * @param {boolean} preferences.marketing - Marketing cookies
 */
export const setConsent = (preferences) => {
	try {
		const consent = {
			version: CONSENT_VERSION,
			essential: true, // Always true, cannot be disabled
			analytics: Boolean(preferences?.analytics),
			marketing: Boolean(preferences?.marketing),
			timestamp: Date.now(),
		};

		// Store in localStorage
		localStorage.setItem(CONSENT_STORAGE_KEY, JSON.stringify(consent));

		// Also store as cookie for server-side access if needed
		setCookie(CONSENT_COOKIE_NAME, JSON.stringify(consent), COOKIE_EXPIRY_DAYS);

		// Apply consent preferences to analytics services
		updateAnalyticsServices(consent);

		return consent;
	} catch (error) {
		console.error("[cookieService][setConsent] - Error setting consent:", error);
		throw error;
	}
};

/**
 * Check if user has given consent (any choice made)
 * @returns {boolean} True if consent has been given
 */
export const hasConsent = () => {
	const consent = getConsent();
	return consent !== null && consent !== undefined;
};

/**
 * Check if analytics consent is granted
 * @returns {boolean} True if analytics consent is granted
 */
export const hasAnalyticsConsent = () => {
	const consent = getConsent();
	return consent?.analytics === true;
};

/**
 * Check if marketing consent is granted
 * @returns {boolean} True if marketing consent is granted
 */
export const hasMarketingConsent = () => {
	const consent = getConsent();
	return consent?.marketing === true;
};

/**
 * Clear consent preferences (withdraw consent)
 */
export const clearConsent = () => {
	try {
		localStorage.removeItem(CONSENT_STORAGE_KEY);
		deleteCookie(CONSENT_COOKIE_NAME);
		// Disable all analytics services
		updateAnalyticsServices({
			essential: true,
			analytics: false,
			marketing: false,
		});
	} catch (error) {
		console.error("[cookieService][clearConsent] - Error clearing consent:", error);
	}
};

/**
 * Set a cookie
 * @param {string} name - Cookie name
 * @param {string} value - Cookie value
 * @param {number} days - Number of days until expiration
 * @param {Object} options - Additional cookie options
 */
export const setCookie = (name, value, days = COOKIE_EXPIRY_DAYS, options = {}) => {
	try {
		const expires = new Date();
		expires.setTime(expires.getTime() + days * 24 * 60 * 60 * 1000);

		const cookieOptions = {
			expires: expires.toUTCString(),
			path: options.path || "/",
			sameSite: options.sameSite || "Lax",
			secure: options.secure !== undefined ? options.secure : window.location.protocol === "https:",
			...options,
		};

		let cookieString = `${name}=${encodeURIComponent(value)};expires=${cookieOptions.expires};path=${cookieOptions.path};sameSite=${cookieOptions.sameSite}`;
		if (cookieOptions.secure) {
			cookieString += ";secure";
		}

		document.cookie = cookieString;
	} catch (error) {
		console.error("[cookieService][setCookie] - Error setting cookie:", error);
	}
};

/**
 * Get a cookie value
 * @param {string} name - Cookie name
 * @returns {string|null} Cookie value or null if not found
 */
export const getCookie = (name) => {
	try {
		const nameEQ = name + "=";
		const cookies = document.cookie.split(";");
		for (let i = 0; i < cookies.length; i++) {
			let cookie = cookies[i];
			while (cookie.charAt(0) === " ") {
				cookie = cookie.substring(1, cookie.length);
			}
			if (cookie.indexOf(nameEQ) === 0) {
				return decodeURIComponent(cookie.substring(nameEQ.length, cookie.length));
			}
		}
	} catch (error) {
		console.error("[cookieService][getCookie] - Error getting cookie:", error);
	}
	return null;
};

/**
 * Delete a cookie
 * @param {string} name - Cookie name
 * @param {Object} options - Cookie options (path, domain, etc.)
 */
export const deleteCookie = (name, options = {}) => {
	try {
		const cookieOptions = {
			path: options.path || "/",
			domain: options.domain,
			...options,
		};

		let cookieString = `${name}=;expires=Thu, 01 Jan 1970 00:00:00 UTC;path=${cookieOptions.path}`;
		if (cookieOptions.domain) {
			cookieString += `;domain=${cookieOptions.domain}`;
		}

		document.cookie = cookieString;
	} catch (error) {
		console.error("[cookieService][deleteCookie] - Error deleting cookie:", error);
	}
};

/**
 * Get all cookies as an object
 * @returns {Object} Object with cookie names as keys and values as values
 */
export const getAllCookies = () => {
	try {
		const cookies = {};
		const cookieStrings = document.cookie.split(";");
		for (let i = 0; i < cookieStrings.length; i++) {
			const cookie = cookieStrings[i].trim();
			if (cookie) {
				const [name, ...valueParts] = cookie.split("=");
				const value = valueParts.join("=");
				cookies[name] = decodeURIComponent(value);
			}
		}
		return cookies;
	} catch (error) {
		console.error("[cookieService][getAllCookies] - Error getting all cookies:", error);
		return {};
	}
};

/**
 * Update analytics services based on consent preferences
 * This function is called whenever consent changes
 * @param {Object} consent - Consent preferences object
 */
const updateAnalyticsServices = (consent) => {
	// This will be called by the analytics services themselves
	// We just ensure the consent state is available
	// The actual initialization/disabling is handled in the respective service files
	if (consent.analytics) {
		console.log("[cookieService][updateAnalyticsServices] - Analytics consent granted");
	} else {
		console.log("[cookieService][updateAnalyticsServices] - Analytics consent denied");
	}
};

/**
 * Check if consent version needs update
 * @returns {boolean} True if consent version is outdated
 */
export const needsConsentUpdate = () => {
	const consent = getConsent();
	if (!consent) {
		return false; // No consent given yet
	}
	return consent.version !== CONSENT_VERSION;
};

/**
 * Get cookie information for a specific category
 * @param {string} category - Cookie category (essential, analytics, marketing)
 * @returns {Array} Array of cookie information objects
 */
export const getCookiesByCategory = (category) => {
	const cookies = {
		essential: [
			{
				name: "i18next",
				purposeKey: "cookie-purpose-i18next",
				expiryKey: "cookie-expiry-1-year",
			},
			{
				name: "i18nextLng",
				purposeKey: "cookie-purpose-i18nextLng",
				expiryKey: "cookie-expiry-persistent",
			},
			{
				name: "rememberedEmail",
				purposeKey: "cookie-purpose-rememberedEmail",
				expiryKey: "cookie-expiry-persistent",
			},
		],
		analytics: [
			{
				name: "_ga",
				purposeKey: "cookie-purpose-ga",
				expiryKey: "cookie-expiry-2-years",
			},
			{
				name: "_gid",
				purposeKey: "cookie-purpose-gid",
				expiryKey: "cookie-expiry-24-hours",
			},
			{
				name: "_gat",
				purposeKey: "cookie-purpose-gat",
				expiryKey: "cookie-expiry-1-minute",
			},
			{
				name: "sentry-trace",
				purposeKey: "cookie-purpose-sentry-trace",
				expiryKey: "cookie-expiry-session",
			},
			{
				name: "sentry-replay",
				purposeKey: "cookie-purpose-sentry-replay",
				expiryKey: "cookie-expiry-session",
			},
		],
		marketing: [
			// Reserved for future marketing cookies
		],
	};

	return cookies[category] || [];
};

const cookieService = {
	getConsent,
	setConsent,
	hasConsent,
	hasAnalyticsConsent,
	hasMarketingConsent,
	clearConsent,
	setCookie,
	getCookie,
	deleteCookie,
	getAllCookies,
	needsConsentUpdate,
	getCookiesByCategory,
};

export default cookieService;

